import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Radar, RefreshCw, Loader2, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const timeAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
};

// Headline "live reach" KPI (Flag/#1) + freshness stamp (#3). Number is real
// Apollo total_entries; the search is platform-paid ($0 to the tenant).
export const ICPReachHeadline = ({ icp }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hist, setHist] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`${API}/api/brain/icp/pool-size`);
      setData(r.data || null);
    } catch { setData(null); }
    finally { setLoading(false); }
    try {
      const h = await api.get(`${API}/api/brain/icp/reach-history`);
      setHist(h.data?.history || []);
    } catch { setHist([]); }
  }, []);

  useEffect(() => { load(); }, [load, icp?.updated_at]);

  const pool = data?.pool_size;
  const sourced = data?.sourced_total;
  const remaining = data?.remaining;
  const fresh = timeAgo(data?.estimated_at);
  const failed = pool == null && !!data?.error;
  const trend = (() => {
    if (!hist || hist.length < 2) return null;
    const prev = hist[hist.length - 2]?.pool_size;
    const cur = hist[hist.length - 1]?.pool_size;
    if (!prev || cur == null) return null;
    const pct = Math.round(((cur - prev) / prev) * 100);
    return { pct, up: pct >= 0 };
  })();

  return (
    <div
      data-testid="icp-reach-headline"
      className="rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.08] to-transparent p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2.5 rounded-xl bg-emerald-500/15 shrink-0">
            <Radar className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
              Live reach — verified, contactable people
            </p>
            {loading ? (
              <p className="text-2xl font-bold text-foreground flex items-center gap-2 mt-1">
                <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-lg font-medium text-muted-foreground">Estimating…</span>
              </p>
            ) : pool == null ? (
              <div className="mt-1" data-testid="icp-reach-unknown">
                <p className="text-3xl font-bold text-foreground/40 leading-none">—</p>
                {failed ? (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="icp-reach-unavailable">
                    <Sparkles className="w-3.5 h-3.5" /> Live estimate temporarily unavailable — the data service didn't respond. Try again shortly.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-500" /> Tap Estimate to see how many prospects match
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-1">
                <p className="text-3xl sm:text-4xl font-bold text-emerald-600 leading-none" data-testid="icp-reach-number">
                  {pool.toLocaleString()}<span className="text-base font-medium text-muted-foreground"> people match</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1.5" data-testid="icp-reach-breakdown">
                  {typeof sourced === 'number' && <>{sourced.toLocaleString()} already sourced</>}
                  {typeof remaining === 'number' && (
                    <span className={remaining === 0 ? 'text-amber-600 font-medium' : ''}> · {remaining.toLocaleString()} still to reach</span>
                  )}
                  {fresh && <span className="text-muted-foreground/70"> · as of {fresh}</span>}
                  {trend && (
                    <span className={trend.up ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'}
                      data-testid="icp-reach-trend"> · {trend.up ? '▲' : '▼'} {Math.abs(trend.pct)}% vs last</span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="icp-reach-refresh-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          {pool == null ? 'Estimate' : 'Refresh'}
        </button>
      </div>
    </div>
  );
};

export default ICPReachHeadline;
